import { useState, type FormEvent } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { db } from '../lib/supabase';
import { Button, ErrorText, Notice, TextField } from '../components/ui';

export function Login() {
  const [params] = useSearchParams();
  const [mode, setMode] = useState<'signin' | 'signup'>(params.get('mode') === 'signup' ? 'signup' : 'signin');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');
  const navigate = useNavigate();
  const next = params.get('next') || '/today';

  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true); setError(''); setInfo('');
    try {
      if (mode === 'signin') {
        const { error: err } = await db().auth.signInWithPassword({ email, password });
        if (err) throw err;
        navigate(next, { replace: true });
      } else {
        const { data, error: err } = await db().auth.signUp({
          email, password, options: { emailRedirectTo: `${window.location.origin}${next}` },
        });
        if (err) throw err;
        if (data.session) navigate(next, { replace: true });
        else setInfo('Check your email and tap the link to confirm your account. Then sign in here.');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="min-h-screen bg-[radial-gradient(ellipse_at_20%_30%,#e7f2fb_0%,transparent_52%),#f7f9fc]">
      <header className="mx-auto flex max-w-[1060px] items-center justify-between px-5 py-7 sm:px-8">
        <Link to="/" className="inline-flex items-center gap-2.5 text-xl font-extrabold tracking-tight text-[#173a56]"><span className="inline-flex h-9 w-9 items-center justify-center rounded-xl bg-[#173a56] text-[16px] text-white">R<span className="text-[#e9ac66]">.</span></span> ReAstra</Link>
        <Link to="/" className="text-[13px] font-bold text-[#648096] hover:text-accent">← Back to website</Link>
      </header>
      <main className="mx-auto grid max-w-[1060px] items-center gap-12 px-5 pb-20 pt-8 sm:px-8 lg:min-h-[660px] lg:grid-cols-[1fr_440px] lg:gap-20 lg:pt-0">
        <div className="hidden max-w-[530px] lg:block">
          <p className="mb-5 text-[11px] font-extrabold uppercase tracking-[.16em] text-[#4881aa]">Your AI sales desk</p>
          <h1 className="text-[49px] font-extrabold leading-[1.13] tracking-[-.05em] text-[#173651]">A clearer day for every property sales team.</h1>
          <p className="mt-6 text-[16px] leading-[1.8] text-[#748a9c]">Project facts, buyer replies, follow-ups and site visits in one workspace. AI helps your reps move faster while approved facts and human reviews keep the work trustworthy.</p>
          <div className="mt-9 flex flex-col gap-4 text-[14px] font-semibold text-[#42647c]">
            <p className="flex items-center gap-3"><span className="grid h-7 w-7 place-items-center rounded-full bg-white text-[#367ca9] shadow-sm">✓</span> One place for the team's next actions</p>
            <p className="flex items-center gap-3"><span className="grid h-7 w-7 place-items-center rounded-full bg-white text-[#367ca9] shadow-sm">✓</span> AI drafts grounded in project facts</p>
            <p className="flex items-center gap-3"><span className="grid h-7 w-7 place-items-center rounded-full bg-white text-[#367ca9] shadow-sm">✓</span> A record from enquiry to commission</p>
          </div>
        </div>
        <div className="w-full rounded-[24px] border border-[#e0e9f1] bg-white p-6 shadow-[0_18px_55px_rgba(26,61,92,.07)] sm:p-8">
          <p className="text-[11px] font-extrabold uppercase tracking-[.14em] text-[#5485a8]">ReAstra workspace</p>
          <h2 className="mt-2 text-[29px] font-extrabold tracking-[-.035em] text-[#1b3c56]">{mode === 'signin' ? 'Welcome back' : 'Create your account'}</h2>
          <p className="mb-6 mt-2 text-[14px] text-[#8294a3]">{mode === 'signin' ? 'Pick up where your sales day left off.' : 'Set up your firm and start with the simulator.'}</p>
          <form onSubmit={submit} className="flex flex-col gap-4">
            <div className="grid grid-cols-2 gap-1 rounded-xl bg-[#f0f4f8] p-1 text-sm font-bold">
              {(['signin', 'signup'] as const).map((m) => (
                <button type="button" key={m} onClick={() => setMode(m)} aria-pressed={mode === m}
                  className={`rounded-lg py-2.5 ${mode === m ? 'bg-white text-[#245c88] shadow-sm' : 'text-[#8092a1]'}`}>
                  {m === 'signin' ? 'Sign in' : 'Create account'}
                </button>
              ))}
            </div>
            <TextField label="Email" type="email" autoComplete="email" required value={email} onChange={(e) => setEmail(e.target.value)} />
            <TextField label="Password" type="password" autoComplete={mode === 'signin' ? 'current-password' : 'new-password'}
              required minLength={8} value={password} onChange={(e) => setPassword(e.target.value)}
              hint={mode === 'signup' ? 'At least 8 characters. Email confirmation may be required.' : undefined} />
            <ErrorText>{error}</ErrorText>
            {info && <Notice>{info}</Notice>}
            <Button className="mt-1 w-full" variant="primary" type="submit" disabled={busy}>{busy ? 'Please wait…' : mode === 'signin' ? 'Sign in to ReAstra' : 'Create account'}</Button>
          </form>
          <p className="mt-6 text-center text-[11px] leading-relaxed text-[#91a0ac]">ReAstra is a product of Astra Ready Technologies Pvt Ltd.</p>
        </div>
      </main>
      <div className="pb-7 text-center text-[11px] text-[#97a6b2]">Built for real-estate channel-partner teams in India.</div>
    </div>
  );
}
